<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import type { InsightActionGate } from '../composables/useInsightActionGate'
import { useInsightActionStore } from '../stores/insightActionStore'

import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui/button'

const props = defineProps<{
  gate: InsightActionGate
}>()

const { t } = useI18n()
const store = useInsightActionStore()

const groupCount = computed(() => store.groups.filter((group) => group.rows.length > 0).length)

const progress = computed(() => (props.gate === 'open' ? store.runProgress : null))

const disclosed = computed(() => props.gate === 'open' || props.gate === 'loading')
const waitedLong = ref(false)
let slowTimer: ReturnType<typeof setTimeout> | undefined
watch(
  () => props.gate === 'loading',
  (loading) => {
    if (slowTimer) clearTimeout(slowTimer)
    waitedLong.value = false
    if (loading) slowTimer = setTimeout(() => (waitedLong.value = true), 5000)
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  if (slowTimer) clearTimeout(slowTimer)
})

const label = computed(() =>
  groupCount.value > 1
    ? t('insightActions.footer.takeActionWithGroups', { count: groupCount.value })
    : t('insightActions.footer.takeAction'),
)

function openAct(): void {
  store.setMode('act')
  store.setStep('select')
}

function openProgress(): void {
  store.setMode('act')
  store.setStep(store.isRunTerminal ? 'result' : 'progress')
}
</script>

<template>
  <div class="insight-action-footer flex w-full items-center justify-start gap-md">
    <div v-if="gate === 'loading'" class="flex items-center gap-sm" aria-live="polite">
      <span
        class="flex h-8 w-28 items-center justify-center gap-xs rounded-sm border border-border text-xs text-muted"
        data-testid="take-action-loading"
      >
        <span
          class="inline-block h-2 w-2 animate-pulse rounded-full bg-muted"
          aria-hidden="true"
        ></span>
        {{ t('insightActions.footer.checking') }}
      </span>
      <span v-if="waitedLong" class="text-xs text-muted" data-testid="take-action-slow">
        {{ t('insightActions.footer.slow') }}
      </span>
    </div>

    <Button
      v-else-if="progress"
      size="sm"
      variant="primary"
      data-testid="take-action"
      @click="openProgress"
    >
      {{ t('insightActions.footer.applying', { done: progress.done, total: progress.total }) }}
    </Button>

    <Button
      v-else-if="gate === 'open'"
      size="sm"
      variant="primary"
      data-testid="take-action"
      @click="openAct"
    >
      {{ label }}
    </Button>

    <Button
      v-if="disclosed && store.historyRunCount > 0"
      size="xs"
      variant="ghost"
      data-testid="action-history"
      @click="store.setMode('history')"
    >
      {{ t('insightActions.footer.history', { count: store.historyRunCount }) }}
    </Button>
  </div>
</template>
